import { ButtonV2, Input, SelectV2, TextArea } from '@cimplico/components';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Firm } from '../../../../types/firm';
import { useUpdateFirm } from '../../../../queries/use-update-firm';
import { Controller, useForm } from 'react-hook-form';
import { toastNotification } from '../../../../components/toast/Toast';
import { UserOverview } from '../../../../types/user';
import { useState } from 'react';
import { useIsFirmRole } from '../../../../hooks/use-is-firm-role';
import { useIsActiveSubscription } from '../../../../hooks/use-is-active-subscription';
import UserSelect from '../../../../components/user-select/UserSelect';
import ChangeFirmAdminConfirmDialog from './ChangeFirmAdminConfirmDialog';
import SectionLabel from '../../../../components/section-label/SectionLabel';
import SettingsContainer from '../../../../components/settings-container/SettingsContainer';

type FirmFormFields = { name: string; admin: UserOverview[] };

type FirmDetailsProps = { firm: Firm };

function FirmDetails({ firm }: FirmDetailsProps): JSX.Element {
    const [newAdmin, setNewAdmin] = useState<string | undefined>(undefined);
    const { isAdmin } = useIsFirmRole();
    const activeSubscription = useIsActiveSubscription();

    const {
        control,
        formState: { errors },
        handleSubmit,
        getValues,
        trigger,
    } = useForm<FirmFormFields>({
        defaultValues: { name: firm.name, admin: firm.admin ? [firm.admin] : undefined },
    });

    const updateFirmQuery = useUpdateFirm(firm.id);

    function onSubmit(): void {
        const values = getValues();

        const { name, admin } = getValues();

        if (name !== firm.name) {
            updateFirmQuery.mutate(
                { name },
                {
                    onSuccess: () => {
                        toastNotification('success', 'Firm name updated!');
                    },
                    onError: () => {
                        toastNotification('error', 'Unable to update firm name!');
                    },
                }
            );
        }

        if (admin.length === 1 && admin[0].id !== firm.admin?.id) {
            setNewAdmin(values.admin[0].id);
        }
    }

    return (
        <SettingsContainer isLastItem>
            {newAdmin !== undefined && <ChangeFirmAdminConfirmDialog userId={newAdmin} onClose={(): void => setNewAdmin(undefined)} />}
            <SectionLabel
                title="Firm details"
                // description="Update your firm’s information, such as its name, description, and allocated administrator."
                description="Update your firm’s information, such as its name, and allocated administrator."
            />
            <form className="grid grid-cols-2 gap-8" onSubmit={handleSubmit(onSubmit)}>
                <div className="col-span-2">
                    <Controller
                        name="name"
                        rules={{ required: 'Firm name is required.' }}
                        control={control}
                        render={({ field }): JSX.Element => (
                            <Input
                                size="small"
                                type="text"
                                label="Firm name"
                                defaultValue={field.value}
                                isError={Boolean(errors.name)}
                                errorMessage={errors?.name?.message?.toString()}
                                onChange={(value): void => field.onChange(value)}
                                disabled={true || !isAdmin || !activeSubscription}
                            />
                        )}
                    />
                </div>
                {/* <div className="col-span-2">
                    <TextArea title="Firm description" placeholder="Enter a description..." />
                </div> */}
                <div className="col-span-2">
                    <Controller
                        name="admin"
                        rules={{ required: 'Firm administrator is required.' }}
                        control={control}
                        render={({ field: { ref, onChange, value } }): JSX.Element => (
                            <UserSelect
                                isDisabled={!isAdmin}
                                innerRef={ref}
                                title="Firm administrator"
                                placeholder="Select administrator..."
                                defaultValue={value}
                                errorMessage={errors.admin?.message?.toString()}
                                onChange={(value): void => {
                                    onChange(value);
                                    trigger('admin');
                                }}
                            />
                        )}
                    />
                </div>
                {isAdmin && (
                    <div>
                        <ButtonV2 label="Save" size="sm" leftIcon={<CheckIcon />} isSubmit />
                    </div>
                )}
            </form>
        </SettingsContainer>
    );
}

export default FirmDetails;
